import { Button, Table, Tag } from 'antd';
import { ShieldAlert, UserRoundCog } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { TransferOwnershipModal } from '../../components/TransferOwnershipModal';
import type { TruckOwnershipPeriod } from './trucks.api';
import { useTruckOwnership } from './use-truck-ownership';

interface TruckOwnershipTabProps {
  truckId: string;
  truckRegistration: string;
  canManage: boolean;
}

const PAST_PERIOD_COLUMNS = [
  { title: 'Owner', dataIndex: 'ownerName', key: 'ownerName' },
  { title: 'From', dataIndex: 'effectiveFrom', key: 'effectiveFrom', render: (date: string) => new Date(date).toLocaleDateString() },
  { title: 'To', dataIndex: 'effectiveTo', key: 'effectiveTo', render: (date: string) => new Date(date).toLocaleDateString() },
];

function currentOwner(periods: TruckOwnershipPeriod[]): TruckOwnershipPeriod | undefined {
  return periods.find((period) => period.effectiveTo === null);
}

/** The truck's ownership periods (gitaumoses4/chukua#15) and, for `truck.manage` holders, the transfer that opens the next one. */
export function TruckOwnershipTab({ truckId, truckRegistration, canManage }: TruckOwnershipTabProps) {
  const { state, transferring, transfer } = useTruckOwnership(truckId, truckRegistration);
  const [modalOpen, setModalOpen] = useState(false);

  if (state.status === 'forbidden' || state.status === 'error') {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="Couldn't load ownership"
        description="Something went wrong loading this truck's ownership. Try reloading the page."
      />
    );
  }

  if (state.status === 'loading') return null;

  const { periods } = state;
  const owner = currentOwner(periods);
  const pastPeriods = periods.filter((period) => period.effectiveTo !== null);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        {owner ? (
          <div className="flex items-center gap-2">
            <Tag color="success">Current owner</Tag>
            <span className="font-medium">{owner.ownerName}</span>
            <span className="text-sm text-muted">since {new Date(owner.effectiveFrom).toLocaleDateString()}</span>
          </div>
        ) : (
          <EmptyState icon={UserRoundCog} title="No owner" description="This truck has no running ownership period." />
        )}
        {canManage && (
          <Button icon={<UserRoundCog className="size-4" />} onClick={() => setModalOpen(true)}>
            Transfer
          </Button>
        )}
      </div>
      {pastPeriods.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-medium text-muted">Past owners</h3>
          <Table rowKey="id" dataSource={pastPeriods} columns={PAST_PERIOD_COLUMNS} pagination={false} />
        </div>
      )}
      <TransferOwnershipModal
        open={modalOpen}
        submitting={transferring}
        onTransfer={async (input, ownerName) => {
          const transferred = await transfer(input, ownerName);
          if (transferred) setModalOpen(false);
          return transferred;
        }}
        onCancel={() => setModalOpen(false)}
      />
    </div>
  );
}
